import { COLORS, RADIUS, btn } from '../../utils/uiStyles';
import { projectColorOf } from './projectColors';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const BAR_TOP = 30;
const LANE_HEIGHT = 23;

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function scheduleDateKey(value) {
  const key = typeof value === 'string' ? value.slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
}

export function buildMonthCells(year, month) {
  const firstDay = new Date(year, month, 1, 12);
  const gridStart = new Date(year, month, 1 - firstDay.getDay(), 12);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return {
      date: localDateKey(date),
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      inCurrentMonth: date.getFullYear() === year && date.getMonth() === month,
    };
  });
}

export function layoutSchedulesForMonth(schedules = [], cells = []) {
  if (!cells.length) return [];

  const visibleStart = cells[0].date;
  const visibleEnd = cells[cells.length - 1].date;
  const visibleSchedules = schedules
    .map((schedule) => ({
      schedule,
      startKey: scheduleDateKey(schedule.start_date),
      endKey: scheduleDateKey(schedule.end_date),
    }))
    .filter((item) => (
      item.startKey
      && item.endKey
      && item.startKey <= item.endKey
      && item.endKey >= visibleStart
      && item.startKey <= visibleEnd
    ))
    .sort((a, b) => (
      a.startKey.localeCompare(b.startKey)
      || a.endKey.localeCompare(b.endKey)
      || String(a.schedule.id).localeCompare(String(b.schedule.id))
    ));

  const laneEnds = [];
  const positioned = visibleSchedules.map((item) => {
    let lane = laneEnds.findIndex((endKey) => endKey < item.startKey);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = item.endKey;
    return { ...item, lane };
  });

  return cells.map((cell, cellIndex) => ({
    ...cell,
    items: positioned
      .filter((item) => item.startKey <= cell.date && item.endKey >= cell.date)
      .map((item) => ({
        schedule: item.schedule,
        lane: item.lane,
        showTitle: item.startKey === cell.date,
        isSegmentStart: item.startKey === cell.date || cellIndex % 7 === 0,
        isSegmentEnd: item.endKey === cell.date || cellIndex % 7 === 6,
      }))
      .sort((a, b) => a.lane - b.lane),
  }));
}

// projectColors 가 없으면(= 프로젝트 1개 선택) 기존 색 규칙 그대로 — 단일 선택 화면은 안 바뀐다.
// 여러 프로젝트를 한 달력에 겹쳐 그릴 때만 '색 = 프로젝트 식별자'가 종류 색을 이긴다.
function scheduleColors(schedule, projectColors) {
  if (projectColors) {
    const color = projectColorOf(projectColors, schedule.project_id);
    return {
      background: color.bar,
      color: color.text,
      opacity: schedule.done ? 0.55 : 1,
      boxShadow: schedule.kind === 'goal' ? 'inset 0 0 0 2px #fff' : undefined,
    };
  }
  if (schedule.kind === 'goal') {
    return { background: COLORS.primary, color: '#fff' };
  }
  if (schedule.done) {
    return { background: COLORS.successSoft, color: COLORS.successText };
  }
  return { background: COLORS.infoSoft, color: COLORS.info };
}

export default function CalendarGrid({
  year, month, schedules, projectColors = null, projectNames = null, assigneeLabelOf = null,
  onPreviousMonth, onNextMonth,
}) {
  const cells = buildMonthCells(year, month);
  const laidOutCells = layoutSchedulesForMonth(schedules, cells);
  const laneCount = laidOutCells.reduce(
    (max, cell) => cell.items.reduce((cellMax, item) => Math.max(cellMax, item.lane + 1), max),
    0,
  );
  const cellHeight = Math.max(94, BAR_TOP + laneCount * LANE_HEIGHT + 8);

  return (
    <section
      aria-label={`${year}년 ${month + 1}월 일정 달력`}
      style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: '16px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <button type="button" onClick={onPreviousMonth} aria-label="이전 달" style={btn('secondary', 'sm')}>‹</button>
        <h3 style={{ margin: 0, color: COLORS.text, fontSize: '17px' }}>{year}년 {month + 1}월</h3>
        <button type="button" onClick={onNextMonth} aria-label="다음 달" style={btn('secondary', 'sm')}>›</button>
      </div>

      <div style={{ minWidth: '700px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {WEEKDAY_LABELS.map((label, index) => (
            <div
              key={label}
              style={{
                padding: '7px 4px',
                textAlign: 'center',
                color: index === 0 ? COLORS.dangerText : index === 6 ? COLORS.info : COLORS.textSub,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {label}
            </div>
          ))}
        </div>

        <div
          role="grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            borderTop: `1px solid ${COLORS.border}`,
            borderLeft: `1px solid ${COLORS.border}`,
          }}
        >
          {laidOutCells.map((cell, cellIndex) => (
            <div
              role="gridcell"
              key={cell.date}
              aria-label={cell.date}
              style={{
                position: 'relative',
                minWidth: 0,
                height: `${cellHeight}px`,
                boxSizing: 'border-box',
                background: cell.inCurrentMonth ? COLORS.surface : COLORS.bg,
                borderRight: `1px solid ${COLORS.border}`,
                borderBottom: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '8px',
                  color: cell.inCurrentMonth
                    ? (cellIndex % 7 === 0 ? COLORS.dangerText : cellIndex % 7 === 6 ? COLORS.info : COLORS.text)
                    : COLORS.textMuted,
                  fontSize: '12px',
                  fontWeight: cell.inCurrentMonth ? 700 : 500,
                }}
              >
                {cell.day}
              </span>

              {cell.items.map((item) => {
                const colors = scheduleColors(item.schedule, projectColors);
                const projectName = projectNames ? projectNames[item.schedule.project_id] : null;
                const leftRadius = item.isSegmentStart ? '4px' : 0;
                const rightRadius = item.isSegmentEnd ? '4px' : 0;
                // 담당자는 tooltip(title 속성)에만 덧붙인다 — 막대 본문 텍스트·색·레인 배치는 불변이다.
                const barTitle = projectName
                  ? `${projectName} · ${item.schedule.title}`
                  : item.schedule.title;
                const who = assigneeLabelOf ? assigneeLabelOf(item.schedule) : '';
                return (
                  <div
                    key={`${item.schedule.id}-${cell.date}`}
                    aria-hidden={!item.showTitle}
                    title={item.showTitle ? (who ? `${barTitle} · ${who}` : barTitle) : undefined}
                    style={{
                      position: 'absolute',
                      top: `${BAR_TOP + item.lane * LANE_HEIGHT}px`,
                      left: item.isSegmentStart ? '4px' : 0,
                      right: item.isSegmentEnd ? '4px' : 0,
                      height: '19px',
                      boxSizing: 'border-box',
                      borderRadius: `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`,
                      padding: item.showTitle ? '2px 6px' : 0,
                      fontSize: '11px',
                      lineHeight: '15px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textDecoration: item.schedule.done ? 'line-through' : 'none',
                      ...colors,
                    }}
                  >
                    {item.showTitle ? item.schedule.title : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
